import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Shot from "./Shot";
import ExploreOtherTopics from "./ExploreOtherTopics";
import useShots from "../../../hooks/useShots";
import useStore from "../../../store/useStore";
import { useLocation } from "react-router-dom";
import { Spin } from "antd";
import { bookmarkNews } from "../service";
import NoShorts from "./NoShorts";
import SeenAllFeeds from "./SeenAllFeeds";
import useImagePreloader from "../../../hooks/useImagePreloader";
import "./shotsFeed.css";
import { startDwellTimer, trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";

type ShotType =
  | "myFeed"
  | "trending"
  | "allNews"
  | "bookmarks"
  | "topicNews"
  | "shotTags";

const PAGE_SIZE = 11;

/**
 * Full-screen vertical feed.
 *
 * Paging is native CSS scroll snapping (see shotsFeed.css) rather than a drag gesture
 * animating absolutely-positioned layers. The previous approach moved each layer by
 * exactly window.innerHeight pixels, so it only behaved while that number and the slide
 * height agreed; when they drifted apart the page became scrollable and that scroll
 * competed with the drag, which is what made the swipe inconsistent and put a scrollbar
 * down the right edge.
 *
 * Letting the browser own the gesture also gets real momentum, snapping from anywhere on
 * the card, and correct behaviour on rotation for free.
 */
/** Feed tab → taxonomy `source` for ZymShots_ArticleOpened. */
const SHOTS_SOURCE_BY_TAB: Record<string, string> = {
  myFeed: "my_feed_tab",
  trending: "trending_tab",
  allNews: "all_news_tab",
  bookmarks: "bookmarks_tab",
  topicNews: "topic_tab",
  shotTags: "hashtag_tab",
};

const SwipeableShorts = ({
  currentShot = 0,
  type,
  setActiveTab,
  topic = "",
  showTabsBar = true,
}: {
  currentShot?: number;
  type: ShotType;
  setActiveTab?: any;
  topic?: string;
  showTabsBar?: boolean;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLastPage, setIsLastPage] = useState(false);

  const feedRef = useRef<HTMLDivElement>(null);
  const hasScrolledToInitial = useRef(false);
  // `shorts` is re-referenced on every page append, which re-runs the view effect for
  // the same card; only count a card once until the user moves off it.
  const lastTrackedShotId = useRef<string | number | null>(null);

  const { getShots, loader } = useShots();
  const { tatvaShots } = useStore();

  /*
   * Memoised because `|| []` allocates a new array whenever the store slice is missing,
   * and this value is a dependency of the effect that reports a shot as viewed. Without
   * it that effect re-runs on every render and fires the tracking request repeatedly.
   */
  const shorts = useMemo(() => tatvaShots?.[type] || [], [tatvaShots, type]);

  const { state } = useLocation();
  const { tag } = state || {};

  // Preload adjacent shots for smooth swiping
  const { preloadImages } = useImagePreloader([], {
    priority: true,
    maxConcurrent: 3,
  });

  /**
   * A trailing slide is appended once the feed is exhausted, so the end-of-feed panel is
   * reached by the same swipe as everything else instead of by a special-cased index.
   */
  const showEndSlide = isLastPage && type !== "bookmarks" && shorts.length > 0;
  const endSlideIndex = shorts.length;

  const fetchShots = useCallback(
    async ({ reset = false }) => {
      const shots =
        (await getShots(
          type,
          pageNumber,
          PAGE_SIZE,
          type === "topicNews" ? tag?.speciality_id : tag?.id,
          reset
        )) || [];
      useStore.getState().initializeLikes(shots || []);
      useStore.getState().initializeBookmarks(shots || []);
      setIsLastPage((shots || [])?.length < PAGE_SIZE);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, pageNumber, tag]
  );

  // Initial and type-dependent resets
  useEffect(() => {
    if (type) fetchShots({ reset: true });
    setPageNumber(0);
    setIsLastPage(false);
    hasScrolledToInitial.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, currentShot, state]);

  useEffect(() => {
    if (pageNumber > 0) fetchShots({ reset: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  /**
   * Jump to the shot the user opened the feed on. Done without smooth behaviour so it is
   * instant and does not read as an animation on first paint.
   */
  useEffect(() => {
    if (hasScrolledToInitial.current || !feedRef.current || !shorts.length) return;

    const index = shorts.findIndex((shot: any) => shot.id === currentShot);
    hasScrolledToInitial.current = true;
    if (index <= 0) return;

    const slide = feedRef.current.querySelector<HTMLElement>(
      `[data-slide-index="${index}"]`
    );
    if (slide) {
      feedRef.current.scrollTo({ top: slide.offsetTop, behavior: "auto" });
      setCurrentIndex(index);
    }
  }, [shorts, currentShot]);

  /**
   * Tracks which slide is showing. An observer on the scroll container is used rather
   * than a scroll handler so this stays off the main thread during the gesture.
   */
  useEffect(() => {
    const root = feedRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(
            (entry.target as HTMLElement).dataset.slideIndex
          );
          if (!Number.isNaN(index)) setCurrentIndex(index);
        });
      },
      // A slide has to be more than half visible to count as current, so the value never
      // flickers between two slides mid-scroll.
      { root, threshold: 0.6 }
    );

    root
      .querySelectorAll<HTMLElement>("[data-slide-index]")
      .forEach((slide) => observer.observe(slide));

    return () => observer.disconnect();
  }, [shorts.length, showEndSlide]);

  /**
   * Fallback for the observer above, which never fires in the native app's WKWebView.
   * The feed element can also be re-created between renders, so the ref is read on every
   * tick rather than captured once (a captured node goes stale and measures as 0x0).
   * The current slide is the one whose box covers the middle of the feed's visible part,
   * measured against the viewport because the gesture may scroll an ancestor rather than
   * the feed itself. A few getBoundingClientRect calls every 400ms is negligible.
   */
  useEffect(() => {
    const syncCurrentSlide = () => {
      const feed = feedRef.current;
      if (!feed) return;
      const feedRect = feed.getBoundingClientRect();
      const visibleTop = Math.max(feedRect.top, 0);
      const visibleBottom = Math.min(feedRect.bottom, window.innerHeight);
      if (visibleBottom <= visibleTop) return;
      const midY = (visibleTop + visibleBottom) / 2;
      const slides = feed.querySelectorAll<HTMLElement>("[data-slide-index]");
      for (const slide of Array.from(slides)) {
        const r = slide.getBoundingClientRect();
        if (r.top <= midY && r.bottom >= midY) {
          const index = Number(slide.dataset.slideIndex);
          if (!Number.isNaN(index)) setCurrentIndex((prev) => (prev === index ? prev : index));
          break;
        }
      }
    };
    const poll = setInterval(syncCurrentSlide, 400);
    return () => clearInterval(poll);
  }, []);

  /** Attributes shared by the open / close / swipe events for one card. */
  const shotAttrs = (shot: any) => ({
    content_id: shot?.id,
    content_title: shot?.title,
    content_type: "shot",
    therapeutic_area: shot?.speciality?.[0]?.code ?? shot?.speciality?.[0]?.name,
  });

  // The card on screen, so leaving the feed closes it like a swipe would.
  const openShotRef = useRef<any>(null);
  const shotDwellRef = useRef<ReturnType<typeof startDwellTimer> | null>(null);
  useEffect(
    () => () => {
      const shot = openShotRef.current;
      const time_spent_min = shotDwellRef.current?.stopMinutes();
      if (!shot) return;
      trackZymedEvent(ZYMED_EVENTS.SHOTS_ARTICLE_CLOSED, {
        content_id: shot?.id,
        content_title: shot?.title,
        content_type: "shot",
        therapeutic_area: shot?.speciality?.[0]?.code ?? shot?.speciality?.[0]?.name,
        time_spent_min,
      });
    },
    []
  );

  const handleView = useCallback(async () => {
    const shot = shorts?.[currentIndex];
    if (shot?.id && lastTrackedShotId.current !== shot.id) {
      // Swiping away from a card closes it: reported before the next card opens so the
      // pair reads in order, with the time the card was actually on screen.
      const previous = openShotRef.current;
      const time_spent_min = shotDwellRef.current?.stopMinutes();
      if (previous) {
        trackZymedEvent(ZYMED_EVENTS.SHOTS_ARTICLE_CLOSED, {
          ...shotAttrs(previous),
          time_spent_min,
        });
      }
      // The swipe itself, counted from the second card on (the first is the open).
      if (previous) {
        trackZymedEvent(ZYMED_EVENTS.SHOTS_CARD_SWIPED, {
          card_id: shot.id,
          card_title: shot.title,
          swipe_index: currentIndex,
        });
      }
      openShotRef.current = shot;
      shotDwellRef.current = startDwellTimer();
      lastTrackedShotId.current = shot.id;
      trackZymedEvent(ZYMED_EVENTS.SHOTS_ARTICLE_OPENED, {
        source: SHOTS_SOURCE_BY_TAB[type] ?? type,
        content_id: shot.id,
        content_title: shot.title,
        content_type: "shot",
        therapeutic_area: shot.speciality?.[0]?.code ?? shot.speciality?.[0]?.name,
        hashtag: Array.isArray(shot.tags)
          ? shot.tags.map((t: any) => t?.tagName ?? t).filter(Boolean).join(",")
          : undefined,
      });
    }
    if (type !== "bookmarks" && shot?.id)
      await bookmarkNews({
        newsId: shot.id,
        isViewed: true,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, shorts, currentIndex]);

  useEffect(() => {
    if (!shorts?.[currentIndex]) return;

    handleView();

    // Preload the neighbours so the next swipe lands on a decoded image.
    const urlsToPreload = [
      shorts[currentIndex]?.url,
      shorts[currentIndex + 1]?.url,
      shorts[currentIndex - 1]?.url,
      shorts[currentIndex + 2]?.url,
    ].filter(Boolean);

    if (urlsToPreload.length > 0) preloadImages(urlsToPreload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shorts, currentIndex]);

  /**
   * Fetch the next page before the user reaches the end, so paging is invisible. Two
   * slides of runway is enough at swipe speed and avoids fetching on every move.
   */
  useEffect(() => {
    if (isLastPage || shorts.length < PAGE_SIZE) return;
    if (currentIndex >= shorts.length - 2) {
      setPageNumber((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, shorts.length, isLastPage]);

  /**
   * Reaching the end panel of a personalised feed moves the user on to All News, matching
   * the previous behaviour.
   */
  useEffect(() => {
    if (!showEndSlide || currentIndex !== endSlideIndex) return;
    if (!["myFeed", "topicNews", "shotTags", "trending"].includes(type)) return;

    const timer = setTimeout(() => setActiveTab?.("allNews"), 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, showEndSlide, endSlideIndex, type]);

  if (loader) {
    return (
      <Spin size="large" className="mt-2 flex justify-center items-center" />
    );
  }

  if (!shorts?.length) return <NoShorts />;

  /*
   * The feed carries no padding.
   *
   * A scroll container's scrollHeight includes its own padding, so safe-area insets here
   * land *past* the last slide: they shift every snap position and leave the page
   * scrollable by exactly the inset. Measured on an iPad, an env(safe-area-inset-bottom)
   * of 20px made scrollHeight 11548 against 11 slides of 1048 (11528).
   *
   * A full-screen reel is edge-to-edge by design; anything that must clear the status bar
   * or home indicator should be inset inside the slide, not on the scroller.
   */
  return (
    <div ref={feedRef} className="zshots-feed bg-white">
      {shorts.map((shot: any, index: number) => (
        <div
          key={shot.id ?? index}
          className="zshots-slide"
          data-slide-index={index}
        >
          <Shot showTabsBar={showTabsBar} data={shot} topic={topic} />
        </div>
      ))}

      {showEndSlide && (
        <div className="zshots-slide" data-slide-index={endSlideIndex}>
          {type === "allNews" ? (
            <SeenAllFeeds />
          ) : (
            <ExploreOtherTopics type={type} setActiveTab={setActiveTab} />
          )}
        </div>
      )}
    </div>
  );
};

export default SwipeableShorts;
